/**
 * @fileoverview Markdown工具函数库
 * @description 提供Markdown内容处理的核心功能，包括代码块和表格的槽位化处理，
 * 用于在diff操作中保护特殊内容不被错误修改。
 *
 * 主要功能：
 * - 将Markdown中的代码块和表格替换为槽位标识符
 * - 从处理后的文本中恢复原始内容
 * - 支持差异对比结果的内容恢复
 *
 * @author AFFiNE Team
 * @version 2.0.0
 * @since 1.0.0
 */
import { type ChangeObject } from '@affine/core/utils/diff';

import { marked, type Token } from 'marked';

// 定义需要处理的 Token 类型
const targetTypes: Record<string, boolean> = {
  code: true,
  table: true,
  list: true,
  blockquote: true,
};

/**
 * 槽位映射类型，用于存储槽位标识符到原始内容的映射
 * @example
 * ```typescript
 * const slotMap: SlotMap = new Map();
 * slotMap.set('<__SLOT-code__>console.log("hello")</__SLOT__>', 'console.log("hello")');
 * ```
 */
type SlotMap = Map<string, string>;

/**
 * 恢复结果接口
 * @description 从槽位标识符恢复原始内容的结果
 */
interface RecoverResult {
  /** 恢复后的文本内容 */
  value: string;
  /** 内容类型 */
  type: string;
}

/**
 * 扩展的ChangeObject接口，添加block属性
 * @description 扩展diff库的ChangeObject接口，添加块级内容标识
 */
interface ExtendedChangeObject<T> extends ChangeObject<T> {
  /** 是否为块级内容 */
  block?: string;
}

/**
 * 重构器接口
 * @description 定义了内容重构器的核心功能
 */
interface Redactor {
  /** 将内容替换为槽位标识符 */
  redact: (value: string, type: string) => string;
  /** 从文本中恢复原始内容 */
  recover: (value: string) => RecoverResult;
  /** 从文本中恢复原始内容并返回详细信息 */
  recoverMarkdown: (
    result: ChangeObject<string>[]
  ) => ExtendedChangeObject<string>[];
  /** 将Markdown中的代码块和表格替换为槽位标识符 */
  redactMarkdown: (markdown: string) => string;
}

/**
 * 创建内容重构器
 * @description 创建一个重构器实例，用于处理内容的槽位化和恢复
 * @returns 重构器实例
 * @example
 * ```typescript
 * const redactor = createRedactor();
 * const slot = redactor.redact('console.log("hello")', 'code');
 * const recovered = redactor.recover(slot);
 * ```
 */
export function createRedactor(): Redactor {
  const slotMap: SlotMap = new Map<string, string>();
  // 缓存正则表达式以提高性能
  const slotTypeRegex = /<__SLOT-(.+?)__>/;

  /**
   * 构建槽位标识符
   * @param value 原始内容
   * @param type 内容类型
   * @returns 槽位标识符
   * @throws {Error} 当value或type无效时抛出错误
   */
  const buildSlot = (value: string, type: string): string => {
    if (!value || typeof value !== 'string') {
      throw new Error('Invalid value provided to buildSlot');
    }
    if (!type || typeof type !== 'string') {
      throw new Error('Invalid type provided to buildSlot');
    }

    const hash = encodeURIComponent(value);
    // const hash = crypto.createHash('md5').update(value).digest('hex');
    return `<__SLOT-${type}__>${hash}</__SLOT__>\n`;
  };

  /**
   * 将内容替换为槽位标识符
   * @param value 原始内容
   * @param type 内容类型
   * @returns 槽位标识符，如果处理失败则返回原始内容
   */
  const redact = (value: string, type: string): string => {
    try {
      const slot = buildSlot(value, type);
      slotMap.set(slot, value);
      return slot;
    } catch (error) {
      console.warn('Failed to redact content:', error);
      return value;
    }
  };

  /**
   * 从文本中恢复原始内容
   * @param value 包含槽位标识符的文本
   * @returns 恢复结果，包含恢复后的文本和类型信息
   */
  const recover = (value: string): RecoverResult => {
    if (!value || typeof value !== 'string') {
      return { value: value || '', type: '' };
    }

    let processedValue = value;
    let lastType = '';

    try {
      const entries = Array.from(slotMap.entries());
      for (const [slot, original] of entries) {
        if (processedValue.includes(slot)) {
          const typeMatch = slot.match(slotTypeRegex);
          lastType = typeMatch ? typeMatch[1] : '';
          // 使用更安全的替换方法，避免正则表达式特殊字符问题
          processedValue = processedValue.split(slot).join(original);
        }
      }
    } catch (error) {
      console.warn('Failed to recover content:', error);
    }

    return { value: processedValue, type: lastType };
  };

  /**
   * 替换Markdown中的代码块和表格为槽位标识符
   * @description 解析Markdown文本，将其中的代码块和表格替换为槽位标识符，
   * 以便在后续的diff操作中保护这些内容不被错误修改
   * @param markdown Markdown文本
   * @returns 处理后的Markdown文本，其中代码块和表格被替换为槽位标识符
   * @example
   * ```typescript
   * const markdown = '# Title\n```js\nconsole.log("hello");\n```';
   * const processed = replaceSlots(markdown);
   * // 代码块被替换为槽位标识符
   * ```
   */
  function redactMarkdown(markdown: string): string {
    if (!markdown || typeof markdown !== 'string') {
      return '';
    }

    try {
      const tokens: Token[] = marked.lexer(markdown);

      tokens.forEach((item: Token) => {
        if (targetTypes[item.type]) {
          item.raw = redact(item.raw, item.type);
        }
      });

      const output = tokens.reduce((text: string, item: Token) => {
        return text + item.raw;
      }, '');

      return output;
    } catch (error) {
      console.warn('Failed to replace slots in markdown:', error);
      return markdown;
    }
  }

  /**
   * 恢复差异结果中的槽位内容
   * @description 遍历差异对象数组，将其中的槽位标识符恢复为原始内容，
   * 并标记恢复的内容为块级内容
   * @param result 差异对象数组
   * @returns 恢复后的差异对象数组
   * @example
   * ```typescript
   * const diffResult = [
   *   { added: true, value: '<__SLOT-code__>console.log("hello")</__SLOT__>' }
   * ];
   * const restored = restoreSlots(diffResult);
   * // restored[0].value === 'console.log("hello")'
   * // restored[0].block === true
   * ```
   */
  function recoverMarkdown(
    result: ChangeObject<string>[]
  ): ExtendedChangeObject<string>[] {
    if (!Array.isArray(result)) {
      return [];
    }

    try {
      result.forEach((part: ExtendedChangeObject<string>) => {
        if (!part || typeof part.value !== 'string') {
          return;
        }

        // 遍历 diffResult 时候还原slot内容
        const recoveryResult = recover(part.value);

        if (recoveryResult && recoveryResult.value !== part.value) {
          part.value = recoveryResult.value;
          part.block = recoveryResult.type;
        }
      });
    } catch (error) {
      console.warn('Failed to restore slots:', error);
    }

    return result as ExtendedChangeObject<string>[];
  }

  return { redact, recover, recoverMarkdown, redactMarkdown };
}

/**
 * 使用 marked 进行内容类型检测
 */
export function getMarkdownType(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return 'text';

  try {
    // 使用 marked.lexer 解析单行内容
    const tokens = marked.lexer(trimmed);

    if (tokens.length === 0) return 'text';

    const firstToken = tokens[0];

    // 根据 token 类型返回对应的内容类型
    switch (firstToken.type) {
      case 'heading':
        return 'heading';
      case 'code':
        return 'code';
      case 'table':
        return 'table';
      case 'list':
        return 'list';
      case 'blockquote':
        return 'quote';
      case 'hr':
        return 'hr';
      case 'paragraph':
        // 检查段落中是否包含图片
        if (firstToken.tokens) {
          const hasImage = firstToken.tokens.some(
            (token: any) => token.type === 'image'
          );
          if (hasImage) return 'image';
        }
        return 'text';
      default:
        return 'text';
    }
  } catch (error) {
    // 如果 marked 解析失败，回退到简单的字符串检测
    if (trimmed.startsWith('#')) return 'heading';
    if (trimmed.startsWith('```')) return 'code';
    if (trimmed.startsWith('|') && trimmed.includes('|')) return 'table';
    if (
      trimmed.startsWith('-') ||
      trimmed.startsWith('*') ||
      /^\d+\./.test(trimmed)
    )
      return 'list';
    if (trimmed.startsWith('>')) return 'quote';
    if (/!\[.*?\]\(.*?\)/.test(trimmed)) return 'image';

    return 'text';
  }
}

/**
 * 从 Markdown 内容中提取纯文本
 * @param markdown - Markdown 格式的文本
 * @returns 提取出的纯文本内容
 * @example
 * ```typescript
 * const markdown = '# 标题\n\n这是**粗体**文本和*斜体*文本。\n\n```js\nconsole.log("代码");\n```';
 * const text = getMarkdownText(markdown);
 * console.log(text); // "标题\n\n这是粗体文本和斜体文本。\n\nconsole.log("代码");"
 * ```
 */
export function getMarkdownText(markdown: string): string {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  try {
    // 使用 marked 解析 Markdown
    const tokens = marked.lexer(markdown);

    // 递归提取文本内容
    const extractTextFromTokens = (tokens: Token[]): string => {
      return tokens
        .map(token => {
          switch (token.type) {
            case 'heading':
              return token.text;

            case 'paragraph':
              return extractTextFromInlineTokens(token.tokens || []);

            case 'text':
              return token.text;

            case 'code':
              return token.text;

            case 'codespan':
              return token.text;

            case 'blockquote':
              return extractTextFromTokens(token.tokens || []);

            case 'list':
              return (token as any).items
                .map((item: any) => extractTextFromTokens(item.tokens || []))
                .join('\n');

            case 'list_item':
              return extractTextFromTokens(token.tokens || []);

            case 'table':
              const headerText = (token as any).header
                .map((cell: any) =>
                  extractTextFromInlineTokens(cell.tokens || [])
                )
                .join(' | ');
              const rowsText = (token as any).rows
                .map((row: any) =>
                  row
                    .map((cell: any) =>
                      extractTextFromInlineTokens(cell.tokens || [])
                    )
                    .join(' | ')
                )
                .join('\n');
              return [headerText, rowsText].filter(Boolean).join('\n');

            case 'hr':
              return '';

            case 'html':
              // 移除 HTML 标签，只保留文本内容
              return token.text.replace(/<[^>]*>/g, '');

            case 'space':
              return '\n'; // 保留空行

            default:
              // 对于其他类型的 token，尝试提取 text 或 tokens 属性
              if ('text' in token && typeof token.text === 'string') {
                return token.text;
              }
              if ('tokens' in token && Array.isArray(token.tokens)) {
                return extractTextFromTokens(token.tokens);
              }
              return '';
          }
        })
        .filter(text => text !== '')
        .join('\n'); // 过滤空字符串并用换行符连接
    };

    // 提取内联 token 的文本
    const extractTextFromInlineTokens = (tokens: Token[]): string => {
      return tokens
        .map(token => {
          switch (token.type) {
            case 'text':
              return token.text;

            case 'strong':
            case 'em':
            case 'del':
              return extractTextFromInlineTokens(token.tokens || []);

            case 'codespan':
              return token.text;

            case 'link':
              return extractTextFromInlineTokens(token.tokens || []);

            case 'image':
              return token.text || token.title || '';

            case 'br':
              return '\n';

            default:
              if ('text' in token && typeof token.text === 'string') {
                return token.text;
              }
              if ('tokens' in token && Array.isArray(token.tokens)) {
                return extractTextFromInlineTokens(token.tokens);
              }
              return '';
          }
        })
        .join('');
    };

    return extractTextFromTokens(tokens);
  } catch (error) {
    // 如果 marked 解析失败，使用简单的正则表达式清理
    console.warn(
      'Failed to parse markdown with marked, falling back to regex:',
      error
    );

    return (
      markdown
        // 移除代码块
        .replace(/```[\s\S]*?```/g, match => {
          const lines = match.split('\n');
          return lines.slice(1, -1).join('\n');
        })
        // 移除内联代码
        .replace(/`([^`]+)`/g, '$1')
        // 移除标题标记
        .replace(/^#{1,6}\s+/gm, '')
        // 移除粗体和斜体标记
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        // 移除删除线
        .replace(/~~([^~]+)~~/g, '$1')
        // 移除链接，保留文本
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // 移除图片
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
        // 移除引用标记
        .replace(/^>\s+/gm, '')
        // 移除列表标记
        .replace(/^[\s]*[-*+]\s+/gm, '')
        .replace(/^[\s]*\d+\.\s+/gm, '')
        // 移除水平分割线
        .replace(/^[-*_]{3,}$/gm, '')
        // 移除多余的空行
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    );
  }
}
